import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button, TextField } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { MeDTO } from "@shared/types";
import { LegalCheckbox } from "@/components/Legal";
import { AuthLayout } from "./AuthLayout";

export function JoinFamily() {
  const params = useParams();
  const [code, setCode] = useState((params.code ?? "").toUpperCase());
  const [familyName, setFamilyName] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (code.length < 8) {
      setFamilyName(null);
      return;
    }
    api
      .get<{ familyName: string }>(`/auth/invite/${code}`)
      .then((r) => setFamilyName(r.familyName))
      .catch(() => setFamilyName(null));
  }, [code]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<MeDTO>("/auth/join", { code, ...form, acceptLegal: accepted });
      qc.setQueryData(["me"], me);
      navigate("/parent", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not join");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={familyName ? `Join ${familyName}` : "Join your family"}
      subtitle="Co-parents get full admin access: approvals, rewards, and discipline."
    >
      <form onSubmit={submit} className="space-y-4">
        <TextField
          label="Invite code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().trim())}
          autoCapitalize="characters"
          className="font-black tracking-widest"
          hint={familyName ? `✓ Invite for ${familyName}` : "8 characters, from the parent who invited you"}
        />
        <TextField label="Your name" required maxLength={40} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <TextField label="Email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          required
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <LegalCheckbox checked={accepted} onChange={setAccepted}>
          I'm 18 or older and a parent or legal guardian of the kids in this family,
        </LegalCheckbox>
        {error && <p className="text-sm font-semibold text-danger">{error}</p>}
        <Button type="submit" size="lg" block loading={busy} disabled={!accepted}>
          Join family
        </Button>
      </form>
    </AuthLayout>
  );
}
